import PageHero from "@/components/site/PageHero";
import { Link } from "react-router-dom";
import { Stethoscope, HeartHandshake, Landmark, Store, Briefcase, ArrowRight } from "lucide-react";
import { INDUSTRIES } from "@/lib/content";

const ICONS = { healthcare: Stethoscope, nonprofit: HeartHandshake, government: Landmark, "small-business": Store, "professional-services": Briefcase };

const DETAILS = {
  healthcare: ["Clinician & nursing recruitment", "Credentialing coordination", "HIPAA-aligned HR policies", "Union & non-union workforce strategy"],
  nonprofit: ["Grant-compliant staffing structures", "Board & ED transitions", "Volunteer vs. employee classification", "501(c)(3) compensation frameworks"],
  government: ["SCA / prevailing wage compliance", "DCAA-aligned timekeeping", "Contract HR support services", "Program operations staffing"],
  "small-business": ["Fractional HR leadership", "First-time HR infrastructure", "Compliance foundation setup", "Recruiting & onboarding at scale"],
  "professional-services": ["Partner-track development", "Billable-hour talent strategy", "Executive & lateral recruitment", "Retention & compensation design"],
};

const IMG = "https://images.unsplash.com/photo-1666886573531-48d2e3c2b684?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHwzfHxoZWFsdGhjYXJlJTIwcHJvZmVzc2lvbmFsc3xlbnwwfHx8fDE3ODM3MzQxOTB8MA&ixlib=rb-4.1.0&q=85";

export default function IndustriesPage() {
  return (
    <div data-testid="industries-page">
      <PageHero
        eyebrow="Industries Served"
        title="Sector fluency is not a nice-to-have. It's the foundation of good HR advice."
        subtitle="Every industry has its own regulatory environment, talent market, and workforce culture. Groman Associates brings sector-specific fluency to every engagement."
        breadcrumbs={[{ label: "Home", to: "/" }, { label: "Industries" }]}
        image={IMG}
      />

      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-20 lg:py-28 space-y-16">
        {INDUSTRIES.map((ind, i) => {
          const Icon = ICONS[ind.slug] || Briefcase;
          return (
            <section key={ind.slug} data-testid={`industry-section-${ind.slug}`} className="grid lg:grid-cols-12 gap-12 border-t border-gray-200 pt-16">
              <div className="lg:col-span-1">
                <div className="w-12 h-12 bg-charcoal grid place-items-center">
                  <Icon className="w-6 h-6 text-gold" />
                </div>
              </div>
              <div className="lg:col-span-5">
                <div className="font-mono text-xs uppercase tracking-[0.24em] text-gold mb-2">Industry 0{i + 1}</div>
                <h2 className="font-heading text-3xl md:text-4xl font-semibold text-charcoal tracking-tight leading-tight">{ind.name}</h2>
                <p className="mt-5 text-charcoal-soft text-base md:text-lg leading-relaxed">{ind.copy}</p>
              </div>
              <div className="lg:col-span-5 lg:col-start-8">
                <div className="overline mb-4">Representative Engagements</div>
                <ul className="space-y-3">
                  {(DETAILS[ind.slug] || []).map((d) => (
                    <li key={d} className="flex gap-3 items-start pb-3 border-b border-gray-100">
                      <div className="w-1.5 h-1.5 bg-gold mt-2 shrink-0" />
                      <span className="text-charcoal">{d}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </section>
          );
        })}
      </div>

      <section className="bg-charcoal text-white">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-20 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8">
          <div>
            <div className="overline text-gold mb-3">Don't See Your Industry?</div>
            <h3 className="font-heading text-2xl md:text-4xl font-semibold">Our methodology travels well. Let's discuss your workforce.</h3>
          </div>
          <Link to="/contact" data-testid="industries-cta" className="inline-flex items-center gap-2 bg-gold text-white px-6 py-4 text-sm font-medium hover:bg-gold-hover transition-colors">
            Start a Conversation <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}
